import React, { useState, useRef, useCallback, useEffect } from "react";
import { SessionThemeHud } from "../../config/sessionThemes";

export interface FloatingPanelState {
  x: number;
  y: number;
  width: number;
  height: number;
  minimized: boolean;
  /** Which edge the panel is docked to. 'none' = freely floating. */
  snapSide?: "none" | "left" | "right";
}

interface FloatingPanelProps {
  panelId: string;
  title: string;
  icon: string;
  defaultState: FloatingPanelState;
  savedState?: FloatingPanelState;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  /** z-index controlled by parent for stacking order */
  zIndex: number;
  onClose: () => void;
  /** Called when panel is clicked to bring it to the front */
  onFocus: () => void;
  /** Called whenever position, size or minimized state changes */
  onStateChange: (_state: FloatingPanelState) => void;
  children: React.ReactNode;
  /** Optional class applied to the panel body */
  bodyClassName?: string;
  /** HUD overlay tokens — enables glass morphism when provided */
  hud?: SessionThemeHud;
  /** Pixels from edge that triggers auto-snap on drag release */
  snapThreshold?: number;
}

/** Height of the title bar — used to clamp dragging within bounds */
const TITLE_BAR_HEIGHT = 40;
const RESIZE_HANDLE_SIZE = 10;

export const FloatingPanel: React.FC<FloatingPanelProps> = ({
  panelId,
  title,
  icon,
  defaultState,
  savedState,
  minWidth = 240,
  minHeight = 220,
  maxWidth = 800,
  maxHeight = 900,
  zIndex,
  onClose,
  onFocus,
  onStateChange,
  children,
  bodyClassName = "",
  hud,
  snapThreshold = 72,
}) => {
  const initial = savedState ?? defaultState;
  const [x, setX] = useState(initial.x);
  const [y, setY] = useState(initial.y);
  const [width, setWidth] = useState(initial.width);
  const [height, setHeight] = useState(initial.height);
  const [minimized, setMinimized] = useState(initial.minimized);
  const [snapSide, setSnapSide] = useState<"none" | "left" | "right">(initial.snapSide ?? "none");

  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ x, y, width, height, minimized, snapSide });
  useEffect(() => {
    stateRef.current = { x, y, width, height, minimized, snapSide };
  }, [x, y, width, height, minimized, snapSide]);

  const emitChange = useCallback(
    (patch: Partial<FloatingPanelState>) => {
      const next = { ...stateRef.current, ...patch };
      stateRef.current = next;
      onStateChange(next);
    },
    [onStateChange]
  );

  // ── Snap helpers ──────────────────────────────────────────────────────────

  const applySnap = useCallback(
    (side: "left" | "right") => {
      const parent = containerRef.current?.parentElement;
      let newY = stateRef.current.y;
      if (parent) newY = Math.max(0, Math.min(parent.clientHeight - TITLE_BAR_HEIGHT, newY));
      setY(newY);
      setSnapSide(side);
      emitChange({ y: newY, snapSide: side });
    },
    [emitChange]
  );

  const handleUnsnap = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const parent = containerRef.current?.parentElement;
      const pw = parent?.clientWidth ?? window.innerWidth;
      const newX =
        stateRef.current.snapSide === "right" ? Math.max(0, pw - stateRef.current.width - 24) : 24;
      setX(newX);
      setSnapSide("none");
      emitChange({ x: newX, snapSide: "none" });
    },
    [emitChange]
  );

  // ── Drag ──────────────────────────────────────────────────────────────────

  const handleTitleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).tagName === "BUTTON") return;
      e.preventDefault();
      onFocus();

      // Resolve current pixel position (normalise from snap)
      const parent = containerRef.current?.parentElement;
      const pw = parent?.clientWidth ?? window.innerWidth;
      let startLeft =
        stateRef.current.snapSide === "right" ? pw - stateRef.current.width : stateRef.current.x;

      // Clear snap immediately so the panel follows the mouse
      setSnapSide("none");
      setX(startLeft);
      stateRef.current = { ...stateRef.current, x: startLeft, snapSide: "none" };

      const startMouseX = e.clientX;
      const startMouseY = e.clientY;
      const origY = stateRef.current.y;

      const onMouseMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startMouseX;
        const dy = ev.clientY - startMouseY;
        let nx = startLeft + dx;
        let ny = origY + dy;
        if (parent) {
          const ph = parent.clientHeight;
          nx = Math.max(0, Math.min(pw - stateRef.current.width, nx));
          ny = Math.max(0, Math.min(ph - TITLE_BAR_HEIGHT, ny));
        }
        setX(nx);
        setY(ny);
        stateRef.current = { ...stateRef.current, x: nx, y: ny };
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        const finalX = stateRef.current.x;
        const finalW = stateRef.current.width;
        // Auto-snap if released close to an edge
        if (finalX < snapThreshold) {
          applySnap("left");
        } else if (parent && finalX + finalW > parent.clientWidth - snapThreshold) {
          applySnap("right");
        } else {
          emitChange({ x: finalX, y: stateRef.current.y, snapSide: "none" });
        }
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [onFocus, emitChange, snapThreshold, applySnap]
  );

  // ── Resize helpers ────────────────────────────────────────────────────────

  const startResize = useCallback(
    (e: React.MouseEvent, resizeX: boolean, resizeY: boolean, invertX = false, invertY = false) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      onFocus();

      const startCX = e.clientX;
      const startCY = e.clientY;
      const origW = stateRef.current.width;
      const origH = stateRef.current.height;
      const origX = stateRef.current.x;
      const origY = stateRef.current.y;
      const parent = containerRef.current?.parentElement;

      const onMouseMove = (ev: MouseEvent) => {
        let nw = origW,
          nh = origH,
          nx = origX,
          ny = origY;
        if (resizeX) {
          const dx = (ev.clientX - startCX) * (invertX ? -1 : 1);
          nw = Math.max(minWidth, Math.min(maxWidth, origW + dx));
          if (invertX) nx = origX + origW - nw;
        }
        if (resizeY) {
          const dy = (ev.clientY - startCY) * (invertY ? -1 : 1);
          nh = Math.max(minHeight, Math.min(maxHeight, origH + dy));
          if (invertY) ny = origY + origH - nh;
        }
        if (parent) {
          const pw = parent.clientWidth,
            ph = parent.clientHeight;
          nx = Math.max(0, Math.min(pw - nw, nx));
          ny = Math.max(0, Math.min(ph - TITLE_BAR_HEIGHT, ny));
        }
        setWidth(nw);
        setHeight(nh);
        setX(nx);
        setY(ny);
        stateRef.current = { ...stateRef.current, width: nw, height: nh, x: nx, y: ny };
      };

      const onMouseUp = () => {
        emitChange({
          width: stateRef.current.width,
          height: stateRef.current.height,
          x: stateRef.current.x,
          y: stateRef.current.y,
        });
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [minWidth, maxWidth, minHeight, maxHeight, onFocus, emitChange]
  );

  // ── Minimize / Close ─────────────────────────────────────────────────────

  const handleMinimize = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const next = !minimized;
      setMinimized(next);
      emitChange({ minimized: next });
    },
    [minimized, emitChange]
  );

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  const handlePanelMouseDown = useCallback(() => {
    onFocus();
  }, [onFocus]);

  // ── Computed position style ───────────────────────────────────────────────

  const positionStyle: React.CSSProperties =
    snapSide === "right"
      ? { position: "absolute", right: 0, left: "auto", top: y, width, zIndex }
      : snapSide === "left"
        ? { position: "absolute", left: 0, right: "auto", top: y, width, zIndex }
        : { position: "absolute", left: x, right: "auto", top: y, width, zIndex };

  const bodyHeight = minimized ? 0 : height - TITLE_BAR_HEIGHT;

  // ── Glass vs. classic chrome ──────────────────────────────────────────────

  const panelBg = hud ? hud.panelGlass : "#1c1c27";
  const panelBorder = hud
    ? `1px solid ${snapSide === "none" ? hud.panelBorderGlass : hud.snapAccent}`
    : "1px solid #4b5563";
  const panelBoxShadow = hud
    ? `0 12px 48px rgba(0,0,0,.65), inset 0 1px 0 rgba(255,255,255,.05), 0 0 0 1px ${snapSide !== "none" ? hud.snapAccent : "transparent"}`
    : "0 4px 24px rgba(0,0,0,.4)";
  const headerBg = hud ? hud.panelHeaderGradient : "#252535";
  const titleColor = hud ? "rgba(165,180,252,.85)" : "#e4e4e7";
  const snapBtnStyle: React.CSSProperties = {
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "rgba(255,255,255,.06)",
    color: "rgba(255,255,255,.40)",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    transition: "all .12s",
    flexShrink: 0,
  };
  const borderRadius =
    snapSide === "left" ? "0 10px 10px 0" : snapSide === "right" ? "10px 0 0 10px" : "10px";

  return (
    <div
      ref={containerRef}
      data-panel-id={panelId}
      role="dialog"
      aria-label={title}
      style={{ ...positionStyle, userSelect: "none" }}
      onMouseDown={handlePanelMouseDown}
    >
      {/* ── Snap accent bar ─────────────────────────────────────────── */}
      {hud && snapSide !== "none" && (
        <div
          style={{
            position: "absolute",
            top: "10%",
            bottom: "10%",
            width: 4,
            borderRadius: 2,
            background: hud.snapAccent,
            ...(snapSide === "left" ? { left: -3 } : { right: -3 }),
            transition: "opacity .2s",
          }}
        />
      )}

      {/* ── Panel chrome ────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          borderRadius,
          overflow: "hidden",
          border: panelBorder,
          boxShadow: panelBoxShadow,
          background: panelBg,
          backdropFilter: hud ? "blur(24px)" : undefined,
          WebkitBackdropFilter: hud ? "blur(24px)" : undefined,
        }}
      >
        {/* Title bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 10px",
            height: TITLE_BAR_HEIGHT,
            background: headerBg,
            borderBottom: minimized ? "none" : `1px solid rgba(255,255,255,.07)`,
            cursor: "grab",
            flexShrink: 0,
          }}
          onMouseDown={handleTitleMouseDown}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: 14 }}>{icon}</span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: titleColor,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </span>
          </div>

          <div
            style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0, marginLeft: 8 }}
          >
            {/* Snap buttons — only when hud glass mode is active */}
            {hud && (
              <>
                {snapSide !== "left" && (
                  <button
                    aria-label="Snap to left edge"
                    onClick={(e) => {
                      e.stopPropagation();
                      applySnap("left");
                    }}
                    style={snapBtnStyle}
                    title="Snap left"
                  >
                    ◀
                  </button>
                )}
                {snapSide !== "right" && (
                  <button
                    aria-label="Snap to right edge"
                    onClick={(e) => {
                      e.stopPropagation();
                      applySnap("right");
                    }}
                    style={snapBtnStyle}
                    title="Snap right"
                  >
                    ▶
                  </button>
                )}
                {snapSide !== "none" && (
                  <button
                    aria-label="Float free"
                    onClick={handleUnsnap}
                    style={snapBtnStyle}
                    title="Float free"
                  >
                    ◈
                  </button>
                )}
              </>
            )}
            <button
              aria-label={minimized ? "Restore" : "Minimize"}
              onClick={handleMinimize}
              style={{ ...snapBtnStyle, fontSize: 10 }}
            >
              {minimized ? "▲" : "−"}
            </button>
            <button
              aria-label="Close"
              onClick={handleClose}
              style={{ ...snapBtnStyle, fontSize: 10 }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "rgba(239,68,68,.35)";
                (e.currentTarget as HTMLButtonElement).style.color = "#fca5a5";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,.06)";
                (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,.40)";
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          className={`overflow-hidden transition-[height] duration-150 ${bodyClassName}`}
          style={{ height: bodyHeight }}
        >
          <div className={`h-full w-full overflow-auto codex-scroll`}>{children}</div>
        </div>
      </div>

      {/* Resize handles — only when not minimized */}
      {!minimized && (
        <>
          <div
            className="absolute bottom-0 left-3 right-3 cursor-ns-resize"
            style={{ height: RESIZE_HANDLE_SIZE, zIndex: 1 }}
            onMouseDown={(e) => startResize(e, false, true)}
          />
          <div
            className="absolute right-0 top-10 bottom-3 cursor-ew-resize"
            style={{ width: RESIZE_HANDLE_SIZE, zIndex: 1 }}
            onMouseDown={(e) => startResize(e, true, false)}
          />
          <div
            className="absolute bottom-0 right-0 cursor-nwse-resize"
            style={{ width: RESIZE_HANDLE_SIZE + 4, height: RESIZE_HANDLE_SIZE + 4, zIndex: 2 }}
            onMouseDown={(e) => startResize(e, true, true)}
          >
            <svg
              viewBox="0 0 10 10"
              className="absolute bottom-1 right-1 w-3 h-3"
              fill="rgba(255,255,255,.2)"
            >
              <path d="M0 10 L10 0 L10 10 Z" />
            </svg>
          </div>
        </>
      )}
    </div>
  );
};
